// src/components/widgets/ReposicionWidget.js
// Widget "Reposición de stock" — qué hay que volver a comprar, ordenado por urgencia.
// Se muestra por PERMISO (`widget-reposicion`, sección WIDGETS de Permisos) y es una
// lista distinta de la del aviso emergente (esa se elige en el módulo).
//
// NO calcula nada: pide el estado ya masticado a `evaluarAlertasReposicion` (la CF lo
// cachea 15 min entre todos los usuarios). Si no hay nada por comprar, el widget NO se
// dibuja — un widget vacío todos los días es ruido que se aprende a ignorar.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BigQueryService } from '../../services/bigQueryService';
import { useCatalogos } from '../../context/CatalogosContext';
import useEsMovil from '../../hooks/useEsMovil';
import MiniImagenProducto from '../ui/MiniImagenProducto';
import { ESTILO_TILE } from './widgetsUI';
import { BellAlertIcon, BuildingStorefrontIcon, ArrowRightIcon, ChevronDownIcon } from '../ui/Icons';

// Cuántos se muestran antes de "Ver los N restantes".
const TOPE_INICIAL = 6;

const estilosResponsivos = `
  @media (max-width: 768px) {
    .repo-widget-container { margin: 15px 0 !important; }
    .repo-widget-header { flex-direction: column !important; align-items: center !important; text-align: center; }
    .repo-widget-header h3 { font-size: 18px !important; }
    .btn-abrir-reposicion { width: 100% !important; }
    .repo-widget-lista { grid-template-columns: 1fr !important; }
  }
`;

// conEnlace: el botón "Ver alertas" sale solo si la persona puede abrir Alertas de Reposición (candado de ruta de
// Layout.js, lo decide Inicio): el permiso del widget no da el módulo.
function ReposicionWidget({ conEnlace = false }) {
  const isMobile = useEsMovil();
  const { productos } = useCatalogos();       // la foto sale del catálogo ya cargado (0 lecturas)
  const [items, setItems] = useState(null);   // null = cargando
  const [error, setError] = useState(false);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const res = await BigQueryService.evaluarAlertasReposicion(false);
        if (vivo) setItems((res.items || []).filter((i) => i.bajo && !i.silenciado));
      } catch (e) {
        console.error('Error cargando reposición:', e);
        if (vivo) { setError(true); setItems([]); }
      }
    })();
    return () => { vivo = false; };
  }, []);

  if (items === null) {
    return (
      <>
        <style>{estilosResponsivos}</style>
        <div className="repo-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
          <div className="skeleton" style={{ width: '240px', height: '24px', borderRadius: 'var(--radio-sm)', marginBottom: '14px' }} />
          <div className="skeleton" style={{ width: '100%', height: '90px', borderRadius: 'var(--radio-lg)' }} />
        </div>
      </>
    );
  }

  // Nada que comprar (o no se pudo consultar): el widget no ocupa espacio.
  if (error || items.length === 0) return null;

  return (
    <>
      <style>{estilosResponsivos}</style>
      <div className="repo-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
        <div className="repo-widget-header" style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: '10px', marginBottom: isMobile ? '12px' : '16px'
        }}>
          <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex' }}><BellAlertIcon className="w-6 h-6" /></span>
            Hay que comprar ({items.length})
          </h3>
          {conEnlace && (
          <Link
            to="/alertas-reposicion"
            className="btn-abrir-reposicion btn-press hover-bright"
            style={{
              height: '38px', boxSizing: 'border-box', padding: '0 16px',
              background: 'var(--color-primario)', color: 'white', border: 'none',
              borderRadius: 'var(--radio-sm)', cursor: 'pointer', textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              fontSize: '14px', fontWeight: 600
            }}
          >
            Ver alertas <ArrowRightIcon className="w-4 h-4" />
          </Link>
          )}
        </div>

        <div className="repo-widget-lista stagger-list" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          gap: isMobile ? '10px' : '14px'
        }}>
          {(expandido ? items : items.slice(0, TOPE_INICIAL)).map((it) => (
            <div key={it.id} style={ESTILO_TILE}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <MiniImagenProducto
                  url={(productos.find((p) => p.id === it.productoId) || {}).imagenUrl}
                  nombre={it.productoNombre}
                  size={38}
                />
                <strong style={{ fontSize: '14px', color: 'var(--color-texto)' }}>{it.productoNombre}</strong>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--color-peligro-texto)' }}>
                  {it.disponible}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--color-texto-secundario)' }}>
                  disponibles · límite {it.limite}
                </span>
              </div>
              <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-texto-tenue)', display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex' }}><BuildingStorefrontIcon className="w-3 h-3" /></span>
                {it.bodegaNombre || 'Bodega'}
                {it.apartado > 0 && <span>· hay {it.enBodega}, {it.apartado} apartadas</span>}
              </div>
            </div>
          ))}
        </div>

        {/* El resto se abre acá mismo: no hace falta irse al módulo para verlos. */}
        {items.length > TOPE_INICIAL && (
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              className="btn-press"
              onClick={() => setExpandido((v) => !v)}
              style={{
                background: 'none', border: 'none', padding: '4px', cursor: 'pointer',
                color: 'var(--color-primario-texto)', fontSize: '13px', fontWeight: 'bold',
                display: 'inline-flex', alignItems: 'center', gap: '5px'
              }}
            >
              {expandido ? 'Ver menos' : `Ver los ${items.length - TOPE_INICIAL} restantes`}
              <span style={{ display: 'inline-flex', transform: expandido ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                <ChevronDownIcon className="w-4 h-4" />
              </span>
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default ReposicionWidget;
